import { useState } from 'react';
import { OptionOutcome } from '@covered-calls/shared';
import type { BrokerProfile, CurrentOption, Exchange } from '@covered-calls/shared';
import { EarlyExitDialog } from './EarlyExitDialog';
import { OptionOutcomeDialog } from './OptionOutcomeDialog';

/**
 * ManageCloseDialog — the single consolidated entry point behind the
 * "Manage/Close Position" button. It is a thin container that chooses/gates
 * between the existing `EarlyExitDialog` and `OptionOutcomeDialog` flows and
 * delegates to them unchanged; it performs no network calls and owns no error
 * state of its own. All validation, server-error, and 502 market-data-
 * unavailable handling is inherited from the delegated dialogs.
 *
 * - Active covered call (`currentOption.status === 'ACTIVE'`): a chooser offers
 *   three outcomes — "Early exit" (→ EarlyExitDialog, hasActiveOption=true),
 *   "Assigned (called away)" (→ OptionOutcomeDialog, forcedOutcome=ASSIGNED),
 *   and "Expired worthless" (→ OptionOutcomeDialog, forcedOutcome=EXPIRED).
 * - No active covered call: the chooser is skipped entirely and the simple-
 *   close path (EarlyExitDialog, hasActiveOption=false) is rendered directly,
 *   with no early-exit/assigned/expired framing.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 3.1, 4.1, 5.1, 6.1, 6.2, 7.3,
 *            7.4, 7.5, 7.6, 8.1, 8.2
 */

export interface ManageCloseDialogProps {
  // Position identity / shared fields (sourced from PositionDetail's `position`).
  positionId: string;
  ticker: string;
  exchange: Exchange;
  /** position.purchasePrice */
  entryPrice: number;
  quantity: number;
  totalCommissions: number;
  /** The active covered call, if any. Present + ACTIVE => three-outcome chooser. */
  currentOption?: CurrentOption;
  brokerProfiles: BrokerProfile[];
  /**
   * Called after a close/early-exit succeeds. PositionDetail wires this to
   * refetch the position (Req 7.1).
   */
  onCloseSuccess: () => void;
  /**
   * Called after an assigned/expired option-outcome succeeds. PositionDetail
   * wires this to refetch the position AND the option history (Req 7.2).
   */
  onOptionOutcomeSuccess: () => void;
  /** Dismiss without submitting (Req 7.3). */
  onCancel: () => void;
}

type Step = 'chooser' | 'early-exit' | 'assigned' | 'expired';

/** The three outcome choices, in display order, shown only for an active call. */
const CHOICES: { step: Exclude<Step, 'chooser'>; label: string; description: string }[] = [
  {
    step: 'early-exit',
    label: 'Early exit',
    description:
      'Close the position before expiry by selling the underlying and buying back the call.',
  },
  {
    step: 'assigned',
    label: 'Assigned (called away)',
    description: 'The call was exercised — your shares are sold at the strike and the position closes.',
  },
  {
    step: 'expired',
    label: 'Expired worthless',
    description: 'The call lapsed out-of-the-money — keep your shares and premium, position stays open.',
  },
];

export function ManageCloseDialog({
  positionId,
  ticker,
  exchange,
  entryPrice,
  quantity,
  totalCommissions,
  currentOption,
  brokerProfiles,
  onCloseSuccess,
  onOptionOutcomeSuccess,
  onCancel,
}: ManageCloseDialogProps) {
  const hasActiveCall = currentOption?.status === 'ACTIVE';
  // Initial step: 'chooser' when there's an active call (three outcomes), else
  // 'early-exit' — the simple-close path, rendered directly with no chooser.
  const [step, setStep] = useState<Step>(hasActiveCall ? 'chooser' : 'early-exit');

  // The early-exit delegation reconstructs `premium` from the active call, the
  // same value PositionDetail passes today (`currentOption?.premium ?? 0`),
  // preserving the exact prop values and request payload.
  const premium = currentOption?.premium ?? 0;

  /**
   * Cancel/Back from a delegated child. When the chooser was shown (active-call
   * case) a child's Cancel returns to the chooser — effectively "Back" — so a
   * user who picked the wrong outcome can correct it without reopening.
   * Otherwise (simple close, no chooser) it propagates `onCancel` to dismiss
   * the whole dialog.
   */
  const handleChildCancel = () => {
    if (hasActiveCall) {
      setStep('chooser');
    } else {
      onCancel();
    }
  };

  if (step === 'early-exit') {
    return (
      <EarlyExitDialog
        positionId={positionId}
        ticker={ticker}
        exchange={exchange}
        entryPrice={entryPrice}
        quantity={quantity}
        premium={premium}
        totalCommissions={totalCommissions}
        hasActiveOption={hasActiveCall}
        onSuccess={onCloseSuccess}
        onCancel={handleChildCancel}
      />
    );
  }

  if (step === 'assigned' && currentOption) {
    return (
      <OptionOutcomeDialog
        positionId={positionId}
        exchange={exchange}
        quantity={quantity}
        currentOption={currentOption}
        brokerProfiles={brokerProfiles}
        forcedOutcome={OptionOutcome.ASSIGNED}
        onSuccess={onOptionOutcomeSuccess}
        onCancel={handleChildCancel}
      />
    );
  }

  if (step === 'expired' && currentOption) {
    return (
      <OptionOutcomeDialog
        positionId={positionId}
        exchange={exchange}
        quantity={quantity}
        currentOption={currentOption}
        brokerProfiles={brokerProfiles}
        forcedOutcome={OptionOutcome.EXPIRED}
        onSuccess={onOptionOutcomeSuccess}
        onCancel={handleChildCancel}
      />
    );
  }

  // Chooser step — only reachable when there is an active covered call.
  return (
    <div
      role="dialog"
      aria-labelledby="manage-close-dialog-title"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <h2 id="manage-close-dialog-title" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          Manage/Close Position — {ticker}
        </h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
          How did this covered call end? Choose the outcome that matches what happened.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {CHOICES.map((choice) => (
            <button
              key={choice.step}
              type="button"
              onClick={() => setStep(choice.step)}
              style={{
                display: 'block',
                textAlign: 'left',
                padding: '1rem',
                backgroundColor: 'var(--color-surface-2)',
                color: 'var(--color-text)',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                minHeight: '44px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <span style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
                {choice.label}
              </span>
              <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-muted)', lineHeight: '1.5' }}>
                {choice.description}
              </span>
            </button>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'var(--color-muted)',
              color: 'var(--color-on-brand)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
              minHeight: '44px',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
